import { Box, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { CustomTextInput } from './customTextInput';

interface YearBasedInputProps {
  label: string;
  value: Record<string, number> | null;
  onChange: (value: Record<string, number> | null) => void;
  fullWidth?: boolean;
}

export const YearBasedInput: React.FC<YearBasedInputProps> = ({
  label,
  value,
  onChange,
}) => {
  const years = ['year_1', 'year_2', 'year_3', 'year_4', 'year_5'];

  // Local state for each year field - allows typing intermediate values like "5."
  const [localValues, setLocalValues] = useState<Record<string, string | number>>(() => {
    const initial: Record<string, string | number> = {};
    years.forEach(year => {
      if (value?.[year] !== undefined && value?.[year] !== null) {
        initial[year] = value[year];
      }
    });
    return initial;
  });

  // Sync local values when prop value changes
  useEffect(() => {
    const newLocalValues: Record<string, string | number> = {};
    years.forEach(year => {
      if (value?.[year] !== undefined && value?.[year] !== null) {
        newLocalValues[year] = value[year];
      }
    });
    setLocalValues(newLocalValues);
  }, [value]);

  const handleYearChange = (year: string, newValue: string | number) => {
    // Update local state immediately (allows typing "." etc)
    setLocalValues(prev => ({
      ...prev,
      [year]: newValue
    }));

    // Commit straight away when the text is a complete number (or empty), so
    // Save sees the edit without a blur. Partial input such as "5." or "-"
    // would not survive the round trip through a number, so it waits for blur.
    const text = String(newValue).trim();
    const currentValue = value || {};
    if (text === '') {
      if (year in currentValue) {
        const updatedValue = { ...currentValue };
        delete updatedValue[year];
        onChange(Object.keys(updatedValue).length === 0 ? null : updatedValue);
      }
      return;
    }
    const numValue = Number(text);
    if (!isNaN(numValue) && String(numValue) === text && currentValue[year] !== numValue) {
      onChange({ ...currentValue, [year]: numValue });
    }
  };

  const handleYearBlur = (year: string) => {
    const localValue = localValues[year];
    const currentValue = value || {};
    
    // If empty, remove from object
    if (localValue === '' || localValue === null || localValue === undefined) {
      const updatedValue = { ...currentValue };
      delete updatedValue[year];
      
      // Also remove from local state
      setLocalValues(prev => {
        const updated = { ...prev };
        delete updated[year];
        return updated;
      });

      // Already committed while typing
      if (!(year in currentValue)) return;

      onChange(Object.keys(updatedValue).length === 0 ? null : updatedValue);
      return;
    }
    
    // Parse to number
    const numValue = typeof localValue === 'number' ? localValue : parseFloat(String(localValue));
    
    // If valid number, update parent
    if (!isNaN(numValue)) {
      // Already committed while typing; just tidy the display (e.g. "5.0" -> 5)
      if (currentValue[year] === numValue) {
        setLocalValues(prev => ({ ...prev, [year]: numValue }));
        return;
      }
      const updatedValue = {
        ...currentValue,
        [year]: numValue,
      };
      onChange(updatedValue);
    } else {
      // Invalid number - clear the field
      setLocalValues(prev => {
        const updated = { ...prev };
        delete updated[year];
        return updated;
      });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="body2"
        sx={{
          marginBottom: '8px',
          color: '#666',
          fontSize: '14px',
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        {years.map((year, index) => (
          <Box key={year} sx={{ flex: '1 1 auto', minWidth: '80px' }}>
            <CustomTextInput
              label={`Year ${index + 1}`}
              type="number"
              value={localValues[year] ?? ''}
              onChange={(val) => handleYearChange(year, val)}
              onBlur={() => handleYearBlur(year)}
              formatNumber={false}
              fullWidth={true}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

